import { useCallback, useRef, useState } from 'react'
import type { RichTextEditorHandle } from '@/components/ui/RichTextEditor'
import type { ArticleComposerImageProps } from './ArticleComposer'
import type { ArticleFormState } from './form-model'
import { useArticleImageDrop } from './useArticleImageDrop'

type SetForm = React.Dispatch<React.SetStateAction<ArticleFormState>>

/**
 * Everything the composer needs to accept an image dropped into the body.
 *
 * The editor handle is created HERE rather than inside the composer so the upload
 * orchestration lives with the form state and the composer stays prop-driven: it
 * renders the canvas and reports drops, and knows nothing about the upload queue.
 */
function useImageProps(imageFileIds: string[], setForm: SetForm): ArticleComposerImageProps {
	const editorRef = useRef<RichTextEditorHandle>(null)

	// Appends rather than replaces: several uploads can confirm in any order, and
	// each one only knows about itself.
	const onImported = useCallback(
		(fileId: string) =>
			setForm((current) =>
				current.imageFileIds.includes(fileId)
					? current
					: { ...current, imageFileIds: [...current.imageFileIds, fileId] },
			),
		[setForm],
	)

	const { onDropFiles, isUploading } = useArticleImageDrop(imageFileIds, onImported, editorRef)

	return { editorRef, onDropFiles, isUploading }
}

/**
 * The composer's own state: the form, and the wiring an image drop needs.
 *
 * Shared by the create and the edit page so the two cannot drift, and separated
 * from the pages so neither has to know how a dropped file becomes an entry in the
 * article's image list.
 */
export function useArticleComposerState(initial: ArticleFormState): {
	form: ArticleFormState
	set: <K extends keyof ArticleFormState>(key: K, value: ArticleFormState[K]) => void
	/**
	 * Whole-form update, for a change that moves several fields at once — applying
	 * the brand template sets the body AND the type, and doing that as two `set`
	 * calls would render an intermediate form that is neither.
	 */
	setForm: SetForm
	imageProps: ArticleComposerImageProps
} {
	const [form, setForm] = useState<ArticleFormState>(initial)

	const set = useCallback(
		<K extends keyof ArticleFormState>(key: K, value: ArticleFormState[K]) =>
			setForm((current) => ({ ...current, [key]: value })),
		[],
	)

	return { form, set, setForm, imageProps: useImageProps(form.imageFileIds, setForm) }
}
